/**
 * adOptoutStore — pricing config + tenant ad-free opt-out status.
 */
import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";

export interface AdOptoutPricing {
  monthly_price_ghs: number;
  annual_price_ghs: number;
  discount_percent: number;
}

export interface TenantAdOptout {
  id: string;
  tenant_id: string;
  plan_type: "monthly" | "annual" | null;
  price_ghs: number | null;
  status: "active" | "inactive" | "pending" | "cancelled";
  starts_at: string | null;
  ends_at: string | null;
  auto_renew: boolean;
  invoice_id: string | null;
}

interface State {
  pricing: AdOptoutPricing;
  myOptout: TenantAdOptout | null;
  loadPricing: () => Promise<void>;
  savePricing: (p: Partial<AdOptoutPricing>) => Promise<void>;
  loadForTenant: (tenantId: string) => Promise<void>;
  purchase: (tenantId: string, plan: "monthly"|"annual") => Promise<void>;
  cancel: (tenantId: string) => Promise<void>;
  isActive: (tenantId: string | null) => Promise<boolean>;
}

export const useAdOptoutStore = create<State>((set, get) => ({
  pricing: { monthly_price_ghs: 50, annual_price_ghs: 500, discount_percent: 0 },
  myOptout: null,
  loadPricing: async () => {
    const { data } = await supabase.from("ad_optout_pricing").select("*").eq("id", 1).maybeSingle();
    if (data) set({ pricing: data as any });
  },
  savePricing: async (p) => {
    const merged = { ...get().pricing, ...p };
    const { error } = await supabase.from("ad_optout_pricing").update(merged as any).eq("id", 1);
    if (error) throw error;
    set({ pricing: merged });
  },
  loadForTenant: async (tenantId) => {
    const { data } = await supabase.from("tenant_ad_optout").select("*")
      .eq("tenant_id", tenantId).order("created_at", { ascending: false }).limit(1).maybeSingle();
    set({ myOptout: (data ?? null) as any });
  },
  purchase: async (tenantId, plan) => {
    const { pricing } = get();
    const price = plan === "monthly" ? pricing.monthly_price_ghs : pricing.annual_price_ghs;
    const starts = new Date();
    const ends = new Date();
    if (plan === "monthly") ends.setMonth(ends.getMonth() + 1); else ends.setFullYear(ends.getFullYear() + 1);
    const { data, error } = await supabase.from("tenant_ad_optout").insert({
      tenant_id: tenantId,
      plan_type: plan,
      price_ghs: price,
      status: "active",
      starts_at: starts.toISOString(),
      ends_at: ends.toISOString(),
      auto_renew: false,
    } as any).select("*").single();
    if (error) throw error;
    set({ myOptout: data as any });
  },
  cancel: async (tenantId) => {
    const cur = get().myOptout;
    if (!cur) return;
    const { error } = await supabase.from("tenant_ad_optout")
      .update({ status: "cancelled", auto_renew: false } as any)
      .eq("id", cur.id);
    if (error) throw error;
    set({ myOptout: { ...cur, status: "cancelled", auto_renew: false } });
  },
  isActive: async (tenantId) => {
    if (!tenantId) return false;
    const { data } = await supabase.from("tenant_ad_optout")
      .select("status,ends_at").eq("tenant_id", tenantId).eq("status", "active")
      .order("created_at", { ascending: false }).limit(1).maybeSingle();
    if (!data) return false;
    if (data.ends_at && new Date(data.ends_at) < new Date()) return false;
    return true;
  },
}));
